// utils/signalR.js
import * as signalR from '@microsoft/signalr'
import { ref } from 'vue'
import { getConfig } from '@/utils/configTool';

 

export function useSignalR(hubUrl, options = {}) {
  const connection = ref(null)
  const isConnected = ref(false)
  const isReconnecting = ref(false) // 新增重连状态标志
  const error = ref(null)
  const reconnectAttempts = ref(0)  
  const reconnectDelay = ref(0) // 新增重连延迟时间
   // 添加连接成功回调数组
  const connectedCallbacks = ref([])
  //const maxReconnectAttempts = options.maxReconnectAttempts || 5
    // 自定义重连策略（可选）
  const reconnectPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) => {
      reconnectAttempts.value = retryContext.previousRetryCount + 1      
      // 指数退避策略：1秒、2秒、4秒、8秒、16秒
      const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 16000)
      reconnectDelay.value = delay // 更新重连延迟时间
      console.log(`SignalR Reconnecting attempt ${reconnectAttempts.value}, next in ${delay/1000} seconds`)
      return delay
    }
  }
 // 执行所有连接成功回调
  const runConnectedCallbacks = () => {
    connectedCallbacks.value.forEach(cb => {
      try {
        cb()
      } catch (e) {
        console.error('SignalR connected callback error:', e)
      }
    })
  }

  // 注册连接成功回调
  const onConnected = (callback) => {
    if (typeof callback === 'function') {
      connectedCallbacks.value.push(callback)
      
      // 如果已经连接，立即执行回调
      if (isConnected.value) {
        callback()
      }
    }
    return () => {
      // 返回一个取消订阅的函数
      connectedCallbacks.value = connectedCallbacks.value.filter(cb => cb !== callback)
    }
  }

  const startConnection = async () => {
    try {
     // ===== 新增：完善的 hubUrl 兜底判断 =====
      // 1. 定义临时变量存储最终的 hub 地址
      let finalHubUrl = '';

      // 2. 判断 hubUrl 类型（响应式 ref / 普通字符串），提取有效地址
      if (hubUrl && typeof hubUrl === 'object' && 'value' in hubUrl) {
        // 情况1：hubUrl 是 Vue 响应式 ref 对象
        finalHubUrl = hubUrl.value || '';
      } else {
        // 情况2：hubUrl 是普通字符串/基本类型
        finalHubUrl = hubUrl || '';
      }

      // 3. 若最终地址无效，从配置加载兜底地址
      if (!finalHubUrl || finalHubUrl.trim() === '') {
        console.warn('hubUrl 无效，从配置文件加载 SignalR 地址');
        const config = await getConfig();
        // 从配置中获取有效地址，再次兜底防止 config 中无该字段
        finalHubUrl = config?.signalRUrl;  //|| 'https://127.0.0.1/subscriptionHub';
        
        // 4. 若 hubUrl 是响应式 ref，同步更新其 value（保持响应式一致性）
        if (hubUrl && typeof hubUrl === 'object' && 'value' in hubUrl) {
          hubUrl.value = finalHubUrl;
        }
      }
      // ======================================
      
      connection.value = new signalR.HubConnectionBuilder()
        .withUrl(finalHubUrl, options)
        .withAutomaticReconnect(options.reconnectPolicy || reconnectPolicy) // 使用自定义策略
        //.withAutomaticReconnect()
        .build()

            // 添加连接状态监听
      connection.value.onreconnecting((err) => {
        isReconnecting.value = true
        console.log('SignalR 开始重连...', err)
      })

      connection.value.onreconnected(() => {
        isReconnecting.value = false
        reconnectAttempts.value = 0
        console.log('SignalR 重连成功')
        runConnectedCallbacks()
      })

      connection.value.onclose((err) => {
        if (err) {
          isReconnecting.value = false
          console.log('SignalR 连接关闭', err)
        }
      })


      await connection.value.start()
      isConnected.value = true
      error.value = null
         // 连接成功后执行所有回调
      runConnectedCallbacks()
      return true
    } catch (err) {
      error.value = err
      isConnected.value = false
      console.error('SignalR Connection Error:', err)
      return false
    }
  }

  const stopConnection = async () => {
    try {
      if (connection.value) {
        await connection.value.stop()
        isConnected.value = false
        return true
      }
      return false
    } catch (err) {
      console.error('SignalR Disconnection Error:', err)
      return false
    }
  }

  const invoke = async (methodName, ...args) => {
    if (connection.value && isConnected.value) {
      try {
        return await connection.value.invoke(methodName, ...args)
      } catch (err) {
        console.error(`SignalR Invoke ${methodName} Error:`, err)
        throw err
      }
    }
    throw new Error('SignalR connection not established')
  }

  const on = (methodName, callback) => {
    if (connection.value) {
      connection.value.on(methodName, callback)
    }
  }

  const off = (methodName, callback) => {
    if (connection.value) {
      connection.value.off(methodName, callback)
    }
  }

  // 组件卸载时自动断开连接
  // onUnmounted(() => {
  //   stopConnection()
  // })

  return {
    isConnected,
    isReconnecting, // 暴露重连状态
    reconnectAttempts, // 暴露重连尝试次数
    reconnectDelay, // 暴露当前重连延迟时间
    error,    
    start: startConnection,
    stop: stopConnection,
    invoke,
    on,
    off,
    onConnected
  }
}